/**
 * The screens' view of the back office. Every call goes to /api on the same
 * address the page was served from, so nothing here knows about hosts,
 * environments, or credentials — the login layer will add a token to
 * `request` in one place when it exists.
 */

import { idToken, isConfigured, signOut, type AuthConfig } from './auth';

export interface Site {
  id: string;
  name: string;
  policyKind: string;
  timezone: string | null;
  engineSupported: boolean;
  letter: string;
  slots: string[];
  cycleLengthDays: number | null;
}

export interface PhysicianSummary {
  abbreviation: string;
  firstName: string;
  lastName: string;
  status: string;
}

/** Who else is working this week (D56). Contact details only when OSMD has switched them on. */
export interface Colleagues {
  week: string;
  weekEnd: string;
  contactsShown: boolean;
  /** The hospitals this physician is at that week, and who else is there. */
  sites: {
    siteId: string;
    siteName: string;
    colleagues: {
      abbreviation: string;
      firstName: string;
      lastName: string;
      positionSlots: string[];
      from: string;
      to: string;
      email?: string | null;
      phone?: string | null;
    }[];
  }[];
}

export interface MyShift {
  id: string;
  date: string; // YYYY-MM-DD
  start: string | null; // HH:MM, null = untimed on-call
  end: string | null;
  crossesMidnight: boolean;
  positionSlot: string;
  scheduleWeek: { siteId: string; status: string };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await idToken();
  // Merge headers last: a call that sets Content-Type must not drop the Authorization header.
  const res = await fetch(`/api${path}`, { ...init, headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init?.headers ?? {}) } });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (res.status === 401 && isConfigured()) {
    // The session is gone (signed out elsewhere, or the token could not be renewed): back to the sign-in screen.
    signOut();
    window.dispatchEvent(new Event('openmd:signed-out'));
  }
  if (!res.ok) {
    const message = (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string')
      ? (body as { message: string }).message
      : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export interface GridCell {
  physicianId: string;
  week: string;
  siteId: string;
  slots: string[];
  days: number;
}
export interface GridCoverage {
  siteId: string;
  week: string;
  providers: number;
  expected: number;
  open: number;
  periodStart: string | null;
}
export interface GridPeriod {
  siteId: string;
  periodStart: string;
  status: string;
  source: string;
  editCount: number;
}
export interface GridAvailability {
  physicianId: string;
  week: string;
  kind: 'want' | 'blackout';
}
export interface Grid {
  quarter: string;
  weeks: string[];
  physicians: (PhysicianSummary & { notes: string | null; requestNote: string | null; targetWeeks: number | null; responded: boolean })[];
  sites: { id: string; name: string; letter: string; engineSupported: boolean }[];
  cells: GridCell[];
  availability: GridAvailability[];
  periods: GridPeriod[];
  coverage: GridCoverage[];
}

/** A quarter the scheduler has opened for requests (D29). */
export interface RequestWindow {
  quarter: string;
  closesOn: string;
  message: string | null;
  open: boolean;
}
export interface AvailabilityRequest {
  wantedWeeks: string[];
  blackoutWeeks: string[];
  targetWeeks: number | null;
  blockWeeks?: number | null;
  maxConsecutiveWeeks?: number | null;
  note: string | null;
  submittedAt: string;
  updatedAt: string;
}
/** The coverage forecast (D44): can next quarter be covered, and what is missing? */
export interface CoverageForecast {
  quarter: string;
  weeks: string[];
  windowOpen: boolean;
  responses: { answered: number; physicians: number; missing: PhysicianSummary[]; withoutTarget: number; withoutBlockLength: number };
  sites: {
    siteId: string;
    siteName: string;
    engineSupported: boolean;
    coverageConfigured: boolean;
    neverScheduled: boolean;
    positions: {
      positionSlot: string;
      expected: boolean;
      credentialed: number;
      provisional: number;
      alsoRequires: string | null;
      blockedByBackup: PhysicianSummary[];
      /** free = answered and did not black it out; silent = have not answered, so not available. */
      weeks: { week: string; free: number; silent: number; wanted: number; tone: 'none' | 'fragile' | 'unknown' | 'ok' }[];
    }[];
  }[];
  gaps: {
    noCredentialing: PhysicianSummary[];
    backupPrecondition: { siteId: string; siteName: string; positions: string[]; backupSlot: string | null; physicians: PhysicianSummary[] }[];
    nobodyCredentialed: { siteId: string; siteName: string; positionSlot: string; alsoRequires: string | null }[];
    thinPools: { siteId: string; siteName: string; positionSlot: string; credentialed: number; weeksNeeded: number }[];
    neverScheduled: { siteId: string; siteName: string }[];
    noCoverageConfigured: { siteId: string; siteName: string }[];
  };
  notes: { abbreviation: string; firstName: string; lastName: string; note: string }[];
}
/** What the week builder needs to draw its form (D42, step 2). */
export interface WeekBuilder {
  siteId: string;
  siteName: string;
  policyKind: string;
  periodStart: string;
  periodEnd: string;
  days: string[];
  /** 'handoff' = a pair per position; 'cycle' = McLeod's two named physicians for a fortnight. */
  shape: 'handoff' | 'cycle';
  takesNights: boolean;
  status: string | null;
  source: string | null;
  /** Why this period cannot be built here, said plainly — or null when it can. */
  blocked: string | null;
  hasDraft: boolean;
  slots: {
    positionSlot: string;
    /** Only set when a period actually abuts this one — otherwise nobody is handing over. */
    previousHolder: string | null;
    /** Who held it most recently when that was NOT the abutting period: history, not a handoff. */
    lastHeldBy: string | null;
    lastHeldPeriod: string | null;
    storedOutgoing: string | null;
    storedIncoming: string | null;
  }[];
  nights: (string | null)[] | null;
  /** Changes made day by day since the week was last planned — what re-planning would undo. */
  handEdits: number;
  cycle: { rounder1PhysicianId: string | null; rounder2PhysicianId: string | null } | null;
  /** Is anybody handing over at all? False for a hospital's first period and after a gap. */
  handoff: boolean;
  previousPeriodStart: string | null;
  lastPeriodStart: string | null;
  lastPeriodEnd: string | null;
}
export interface WeekPreview {
  shifts: { date: string; positionSlot: string; physicianId: string | null; start: string | null; end: string | null; crossesMidnight: boolean }[];
  valid: boolean;
  flags: Flag[];
}
/** A proposed quarter (D42, step 3). Read-only: nothing is stored until the office accepts it. */
export interface QuarterDraft {
  quarter: string;
  weeks: string[];
  policy: { name: string; order: string[]; blockWeeks: number; preferRunsAtMost: number };
  cells: { siteId: string; positionSlot: string; week: string; spansWeeks?: number; physicianId: string; becauseOf: string }[];
  open: {
    siteId: string;
    positionSlot: string;
    week: string;
    spansWeeks?: number;
    reason:
      | { kind: 'nobody-credentialed' }
      | { kind: 'none-free'; credentialed: number; neverAnswered: number; saidNo: number; elsewhere: number; tooManyInARow: number };
    /** Somebody credentialed for this position who offered part of that week (D60). Never filled from — the office decides. */
    partialOffers?: { abbreviation: string; firstName: string; lastName: string; dates: string[]; note: string | null }[];
  }[];
  people: { physicianId: string; weeks: number; alreadyWorkingWeeks: number; targetWeeks: number | null; wantedWeeksGiven: number; wantedWeeksAsked: number; longestRun: number; flagged: boolean; sites: string[] }[];
  /** A bound on what ANY arrangement could have covered, over the `of` single-week positions. */
  ceiling: number;
  of: number;
  filledOfThose: number;
  filled: number;
  /** Settled positions the facts have overtaken — kept where the office put them, and said. */
  settledProblems: { siteId: string; positionSlot: string; week: string; physicianId: string; why: string }[];
  sites: { siteId: string; siteName: string }[];
  notDrafted: { siteId: string; siteName: string; why: string }[];
}
/** A drafted quarter the office can review, change and accept (D42, step 4). */
export interface QuarterProposal extends Omit<QuarterDraft, 'cells'> {
  id: string;
  createdBy: string;
  createdAt: string;
  supersedesId: string | null;
  cells: { id: string; siteId: string; positionSlot: string; week: string; spansWeeks: number; physicianId: string | null; edited: boolean; pinned: boolean }[];
  /** Weeks that already exist for the periods this proposal covers — whoever wrote them. */
  periods: { siteId: string; periodStart: string; status: string; source: string; fromThisProposal: boolean }[];
}
export interface AcceptOutcome {
  siteId: string;
  periodStart: string;
  status: 'accepted' | 'skipped' | 'refused';
  message: string;
  errors?: string[];
}
/** An offer to cover part of a week the physician cannot take in full (D60). */
export interface PartialOffer {
  week: string;
  dates: string[];
  note: string | null;
}
export interface RequestInput {
  wantedWeeks: string[];
  blackoutWeeks: string[];
  targetWeeks: number | null;
  note: string | null;
  /** Why a week became Can't, keyed by the week's Tuesday (D32). */
  reasons?: Record<string, string> | null;
  /** Sending this replaces every part-week offer for the quarter; leaving it out keeps them (D60). */
  partials?: { week: string; dates: string[]; note?: string | null }[];
}
/** A week the physician is scheduled in (finalized or published): not theirs to change on the board. */
export interface LockedWeek {
  week: string;
  siteId: string;
  siteName: string;
  status: string;
}
export interface AvailabilityChange {
  week: string;
  from: string;
  to: string;
  reason: string | null;
  changedBy: string;
  changedAt: string;
}
export interface MyRequest {
  quarter: string;
  weeks: string[];
  /** Weeks that have already begun (D97): shown with their answer, with nothing left to change. */
  started: string[];
  window: RequestWindow | null;
  request: (AvailabilityRequest & { partials: PartialOffer[] }) | null;
  partials: PartialOffer[];
  locked: LockedWeek[];
  changes: AvailabilityChange[];
}
export interface AvailabilityQuarter {
  quarter: string;
  window: { closesOn: string; open: boolean } | null;
}
export interface RequestSummary {
  quarter: string;
  weeks: string[];
  window: RequestWindow | null;
  responded: number;
  physicians: (PhysicianSummary & { request: (AvailabilityRequest & { partials: PartialOffer[] }) | null; partials: PartialOffer[] })[];
  tally: { week: string; want: number; blackout: number; partial: number }[];
}

export interface PeriodShift {
  id: string;
  date: string;
  start: string | null;
  end: string | null;
  crossesMidnight: boolean;
  positionSlot: string;
  origin: string;
  sourceLabel: string | null;
  physician: { abbreviation: string; firstName: string; lastName: string } | null;
}
export interface Period {
  id: string;
  siteId: string;
  periodStart: string;
  status: string;
  source: string;
  importedFrom: string | null;
  finalizedBy: string | null;
  publishedBy: string | null;
  overrideReason: string | null;
  editCount: number;
  assignments: PeriodShift[];
}
export interface Flag {
  severity: 'error' | 'warning';
  message: string;
  /** The credentialing rule (D43): an error no override reason clears. */
  hard?: true;
}
export interface Candidate {
  abbreviation: string;
  firstName: string;
  lastName: string;
  clear: boolean;
  blockers: string[];
  notes: string[];
}
export interface EditActor {
  editedBy: string;
  reason: string;
  overrideReason?: string;
}
export interface EditOutcome {
  flags: Flag[];
  introduced: Flag[];
}
export interface EditRecord {
  id: string;
  action: string;
  reason: string;
  overrideReason: string | null;
  editedBy: string;
  editedAt: string;
  before: Partial<PeriodShift> & { physicianId?: string | null } | null;
  after: Partial<PeriodShift> & { physicianId?: string | null } | null;
}

const json = (body: unknown, method: string): RequestInit => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

/** The one sign-up marketplace on the platform today (D108). */
export const TRANSFER_CENTER_SITE = 'deaconess-transfer-center';

/** A Transfer Center shift as the sign-up board shows it (D108). */
export interface OpenShift {
  id: string;
  date: string;
  positionSlot: string;
  label: string;
  start: string;
  end: string;
  crossesMidnight: boolean;
  takenBy: string | null;
  mine: boolean;
  takeable: boolean;
  /** Why this one is not theirs to take — a clash, or a start date not yet reached. */
  blockedBy: string | null;
}

export interface BoardShift {
  id: string;
  date: string;
  positionSlot: string;
  label: string;
  start: string | null;
  end: string | null;
  crossesMidnight: boolean;
  takenBy: string | null;
}

export interface CostRow {
  key: string;
  turns: number;
  rounds: number;
  usd: number;
}

export interface CallieCosts {
  days: number;
  since: string;
  turns: number;
  usd: number;
  perTurn: number;
  tokens: { input: number; cacheWrite: number; cacheRead: number; output: number };
  /** Cache reads per token written. Under about 5 means the prompt is being re-cached too often. */
  cacheRatio: number | null;
  unpriced: string[];
  byDay: CostRow[];
  byPerson: CostRow[];
  byKind: CostRow[];
  dearest: { at: string; usd: number; rounds: number; kind: string }[];
}

export const api = {
  grid: (quarter: string) => request<Grid>(`/grid?quarter=${encodeURIComponent(quarter)}`),
  gridQuarters: () => request<{ quarters: string[]; current: string; suggested: string }>('/grid/quarters'),
  period: (siteId: string, periodStart: string) => request<Period>(`/sites/${siteId}/periods/${periodStart}`),
  validation: (siteId: string, periodStart: string) => request<{ valid: boolean; flags: Flag[] }>(`/sites/${siteId}/periods/${periodStart}/validation`),
  candidates: (siteId: string, positionSlot: string, date: string) =>
    request<Candidate[]>(`/sites/${siteId}/candidates?positionSlot=${encodeURIComponent(positionSlot)}&date=${date}`),
  edits: (siteId: string, periodStart: string) => request<EditRecord[]>(`/sites/${siteId}/periods/${periodStart}/edits`),
  patchShift: (siteId: string, periodStart: string, shiftId: string, body: EditActor & { physicianId?: string | null; start?: string | null; end?: string | null }) =>
    request<EditOutcome>(`/sites/${siteId}/periods/${periodStart}/shifts/${shiftId}`, json(body, 'PATCH')),
  addShift: (siteId: string, periodStart: string, body: EditActor & { date: string; positionSlot: string; physicianId: string | null; start: string | null; end: string | null }) =>
    request<EditOutcome>(`/sites/${siteId}/periods/${periodStart}/shifts`, json(body, 'POST')),
  /** Move a handover that slipped (D70): both halves retimed in one call, so no gap or overlap is left. */
  moveHandover: (siteId: string, periodStart: string, shiftId: string, body: EditActor & { at: string }) =>
    request<EditOutcome>(`/sites/${siteId}/periods/${periodStart}/shifts/${shiftId}/handover`, json(body, 'POST')),
  removeShift: (siteId: string, periodStart: string, shiftId: string, body: EditActor) =>
    request<EditOutcome>(`/sites/${siteId}/periods/${periodStart}/shifts/${shiftId}`, json(body, 'DELETE')),
  sites: () => request<Site[]>('/sites'),
  physicians: () => request<PhysicianSummary[]>('/physicians'),
  myColleagues: (abbreviation: string, week: string) =>
    request<Colleagues>(`/physicians/${encodeURIComponent(abbreviation)}/colleagues?week=${week}`),
  myShifts: (abbreviation: string, from: string, to: string) =>
    request<MyShift[]>(`/physicians/${encodeURIComponent(abbreviation)}/shifts?from=${from}&to=${to}`),
  requestWindows: () => request<RequestWindow[]>('/request-windows'),
  setRequestWindow: (quarter: string, body: { closesOn: string; message: string | null }) => request<RequestWindow>(`/request-windows/${quarter}`, json(body, 'PUT')),
  myRequest: (abbreviation: string, quarter: string) => request<MyRequest>(`/physicians/${encodeURIComponent(abbreviation)}/requests/${quarter}`),
  saveRequest: (abbreviation: string, quarter: string, body: RequestInput) =>
    request<AvailabilityRequest>(`/physicians/${encodeURIComponent(abbreviation)}/requests/${quarter}`, json(body, 'PUT')),
  requestSummary: (quarter: string) => request<RequestSummary>(`/requests?quarter=${encodeURIComponent(quarter)}`),
  coverageForecast: (quarter: string) => request<CoverageForecast>(`/coverage-forecast?quarter=${encodeURIComponent(quarter)}`),
  draftQuarter: (quarter: string) => request<QuarterDraft>(`/draft-quarter?quarter=${encodeURIComponent(quarter)}`),
  quarterProposal: (quarter: string) => request<QuarterProposal | null>(`/quarter-proposals?quarter=${encodeURIComponent(quarter)}`),
  proposalById: (id: string) => request<QuarterProposal>(`/quarter-proposals/${id}`),
  createProposal: (body: { quarter: string; supersedes?: string | null; createdBy?: string | null }) => request<QuarterProposal>('/quarter-proposals', json(body, 'POST')),
  setProposalCell: (id: string, cellId: string, body: { physicianId?: string | null; pinned?: boolean }) =>
    request<{ id: string; physicianId: string | null; edited: boolean; pinned: boolean }>(`/quarter-proposals/${id}/cells/${cellId}`, json(body, 'PUT')),
  acceptProposal: (id: string, body: { periods: { siteId: string; periodStart: string }[]; acceptedBy?: string | null; replaceExisting?: boolean }) =>
    request<AcceptOutcome[]>(`/quarter-proposals/${id}/accept`, json(body, 'POST')),
  availabilityQuarters: (abbreviation: string) => request<AvailabilityQuarter[]>(`/physicians/${encodeURIComponent(abbreviation)}/availability-quarters`),
  authConfig: () => request<AuthConfig>('/auth/config'),
  me: () => request<Me>('/me'),
  dashboard: (week?: string) => request<Dashboard>(`/dashboard${week ? `?week=${week}` : ''}`),
  /** A fingerprint of the caller's schedule (D94): the same string means nothing has changed. */
  changeVersion: () => request<{ version: string }>('/changes/version'),
  /** What Callie has cost (D91). CallCovered only — the API refuses anybody else. */
  callieCosts: (days: number) => request<CallieCosts>(`/callie/costs?days=${days}`),
  siteQuarter: (siteId: string, quarter: string) => request<SiteQuarter>(`/sites/${siteId}/calendar?quarter=${quarter}`),
  /** Every hospital's quarter at once — the admin home page's combined grid (D65, D67). */
  allSitesQuarter: (quarter: string) => request<{ quarter: string; sites: SiteQuarter[] }>(`/calendar?quarter=${quarter}`),
  // Building a week (D42, step 2) — the API has been able to do this since D12; nothing ever called it.
  weekBuilder: (siteId: string, periodStart: string) => request<WeekBuilder>(`/sites/${siteId}/periods/${periodStart}/builder`),
  previewWeek: (siteId: string, periodStart: string, input: unknown) => request<WeekPreview>(`/sites/${siteId}/periods/${periodStart}/preview`, json(input, 'POST')),
  saveDraft: (siteId: string, periodStart: string, input: unknown, opts?: { discardEdits?: boolean; savedBy?: string | null }) =>
    request<Period>(`/sites/${siteId}/periods/${periodStart}/draft${opts?.discardEdits ? '?discardEdits=true' : ''}`, json(input, 'POST')),
  finalizeWeek: (siteId: string, periodStart: string, body: { finalizedBy?: string; overrideReason?: string }) => request<Period>(`/sites/${siteId}/periods/${periodStart}/finalize`, json(body, 'POST')),
  /** Take a finalized or published week back to a draft (D52). Deletes its shifts. */
  reopenWeek: (siteId: string, periodStart: string, body: { reopenReason: string; reopenedBy?: string | null; discardEdits?: boolean }) =>
    request<Period>(`/sites/${siteId}/periods/${periodStart}/reopen`, json(body, 'POST')),
  publishWeek: (siteId: string, periodStart: string, body: { publishedBy?: string }) => request<Period>(`/sites/${siteId}/periods/${periodStart}/publish`, json(body, 'POST')),
  tourSheets: (siteId: string, periodStart: string) => request<TourSheet[]>(`/timesheets?siteId=${siteId}&periodStart=${periodStart}`),
  approveSheet: (siteId: string, periodStart: string, abbreviation: string, approvedBy: string) =>
    request<TourSheet>(`/timesheets/${siteId}/${periodStart}/${encodeURIComponent(abbreviation)}/approve`, json({ approvedBy }, 'POST')),
  myTours: (abbreviation: string, from: string, to: string) => request<TourSummary[]>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets?from=${from}&to=${to}`),
  mySheet: (abbreviation: string, siteId: string, periodStart: string) => request<TourSheet>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}`),
  recordHours: (abbreviation: string, siteId: string, periodStart: string, body: { days: DayHours[]; note: string | null; editedBy: string | null }) =>
    request<TourSheet>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}`, json(body, 'PUT')),
  /** The physician's own "yes, these are right" (D57). They cannot change a number — that is the office's (D35). */
  approveMyHours: (abbreviation: string, siteId: string, periodStart: string) =>
    request<TourSheet>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}/approve`, json({}, 'POST')),
  askAboutHours: (abbreviation: string, siteId: string, periodStart: string, message: string) =>
    request<TourSheet>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}/question`, json({ message }, 'POST')),
  resolveHoursQuestion: (abbreviation: string, siteId: string, periodStart: string, resolvedBy: string) =>
    request<TourSheet>(`/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}/question/resolve`, json({ resolvedBy }, 'POST')),
  sheetPath: (abbreviation: string, siteId: string, periodStart: string) => `/physicians/${encodeURIComponent(abbreviation)}/timesheets/${siteId}/${periodStart}/sheet.xlsx`,
  packetPath: (siteId: string, periodStart: string) => `/timesheets/packet?siteId=${siteId}&periodStart=${periodStart}`,
  /** Shift confirmations for a tour, zipped (D116). Published weeks only; the route says so if not. */
  confirmationsPath: (siteId: string, periodStart: string) => `/sites/${encodeURIComponent(siteId)}/confirmations/${periodStart}`,
  // Tour logistics (D33)
  logisticsWeek: (siteId: string, week: string) => request<LogisticsWeek>(`/logistics/week?siteId=${siteId}&week=${week}`),
  housingCapacity: (week: string) => request<HousingCapacity>(`/logistics/housing-capacity?week=${week}`),
  logisticsConfig: () => request<LogisticsConfig>('/logistics/config'),
  checkFlights: () => request<{ checked: number }>('/logistics/flights/check', { method: 'POST' }),
  siteAddress: (siteId: string) => request<SiteAddress>(`/sites/${siteId}/address`),
  saveSiteAddress: (siteId: string, address: string | null) => request<SiteAddress>(`/sites/${siteId}/address`, json({ address }, 'PUT')),
  assignStay: (body: { physicianId: string; roomId: string; arriveOn: string; departOn: string; note?: string | null; status?: 'proposed' | 'confirmed' }) => request<{ id: string }>('/logistics/stays', json(body, 'POST')),
  confirmStay: (id: string) => request<{ id: string }>(`/logistics/stays/${id}/confirm`, { method: 'POST' }),
  removeStay: (id: string) => request<{ removed: string }>(`/logistics/stays/${id}`, { method: 'DELETE' }),
  proposeStays: (siteId: string, week: string) => request<{ proposed: { physician: Person; room: string; residence: string }[]; stuck: { physician: Person; why: string }[] }>(`/logistics/propose?siteId=${siteId}&week=${week}`, { method: 'POST' }),
  shareResidence: (siteId: string, id: string, withSiteId: string) =>
    request<Residence>(`/sites/${siteId}/residences/${id}/serves`, json({ siteId: withSiteId }, 'POST')),
  unshareResidence: (siteId: string, id: string, otherSiteId: string) =>
    request<Residence>(`/sites/${siteId}/residences/${id}/serves/${otherSiteId}`, { method: 'DELETE' }),
  residences: (siteId: string) => request<Residence[]>(`/sites/${siteId}/residences`),
  saveResidence: (siteId: string, id: string | null, body: ResidenceInput) => request<Residence>(id ? `/sites/${siteId}/residences/${id}` : `/sites/${siteId}/residences`, json(body, id ? 'PUT' : 'POST')),
  // Residence photographs (D73). The bytes are fetched by URL, one picture at a time, so a
  // page with ten of them does not carry ten megabytes of JSON.
  // Deaconess Transfer Center sign-up (D108). The only place a physician writes to the
  // schedule themselves, so every one of these routes works out who is calling from the
  // login and never from anything the screen sends.
  transferCenterEligibility: (abbreviation: string) =>
    request<{ slots: string[] }>(`/physicians/${encodeURIComponent(abbreviation)}/transfer-center/eligibility`),
  transferCenterOpen: (abbreviation: string) =>
    request<OpenShift[]>(`/physicians/${encodeURIComponent(abbreviation)}/transfer-center/open`),
  claimTransferShift: (abbreviation: string, shiftId: string) =>
    request<{ taken: boolean; date: string; positionSlot: string }>(`/physicians/${encodeURIComponent(abbreviation)}/transfer-center/shifts/${shiftId}/claim`, { method: 'POST' }),
  releaseTransferShift: (abbreviation: string, shiftId: string) =>
    request<{ released: boolean; date: string; positionSlot: string }>(`/physicians/${encodeURIComponent(abbreviation)}/transfer-center/shifts/${shiftId}/release`, { method: 'POST' }),
  // The office routes name their hospital in the path so the auth guard scopes them — a
  // route carrying no site key is checked against nothing (D77).
  transferCenterBoard: (siteId: string) => request<BoardShift[]>(`/sites/${encodeURIComponent(siteId)}/transfer-center/board`),
  postTransferDates: (siteId: string, dates: string[], slots: string[]) =>
    request<{ posted: number; alreadyThere: number }>(`/sites/${encodeURIComponent(siteId)}/transfer-center/post`, json({ dates, slots }, 'POST')),
  unpostTransferShift: (siteId: string, shiftId: string) =>
    request<{ removed: boolean }>(`/sites/${encodeURIComponent(siteId)}/transfer-center/shifts/${shiftId}`, { method: 'DELETE' }),
  /** A hospital's documents (D98). A physician is told only about hospitals they still visit. */
  siteDocuments: (siteId: string) => request<SiteDocument[]>(`/sites/${siteId}/documents`),
  addSiteDocument: (siteId: string, body: { file: string; name: string | null }) =>
    request<SiteDocument>(`/sites/${siteId}/documents`, json(body, 'POST')),
  removeSiteDocument: (siteId: string, documentId: string) =>
    request<{ removed: boolean }>(`/sites/${siteId}/documents/${documentId}`, { method: 'DELETE' }),
  residencePhotos: (residenceId: string) => request<ResidencePhoto[]>(`/residences/${residenceId}/photos`),
  addResidencePhoto: (residenceId: string, body: { file: string; caption: string | null }) =>
    request<ResidencePhoto>(`/residences/${residenceId}/photos`, json(body, 'POST')),
  removeResidencePhoto: (residenceId: string, photoId: string) =>
    request<{ removed: boolean }>(`/residences/${residenceId}/photos/${photoId}`, { method: 'DELETE' }),
  saveRoom: (residenceId: string, id: string | null, body: { name: string; notes: string | null; active?: boolean }) => request<Room>(id ? `/residences/${residenceId}/rooms/${id}` : `/residences/${residenceId}/rooms`, json(body, id ? 'PUT' : 'POST')),
  guide: (siteId: string) => request<SiteGuide>(`/sites/${siteId}/guide`),
  saveGuide: (siteId: string, body: string) => request<SiteGuide>(`/sites/${siteId}/guide`, json({ body }, 'PUT')),
  // Callie (D34)
  callieConfig: () => request<{ enabled: boolean; name: string; mode: string; voice?: { enabled: boolean; provider: 'openai' | 'polly'; voice: string; voices: string[] } }>('/callie/config'),
  callie: (body: { conversationId: string | null; message: string; abbreviation: string | null; editedBy: string | null }) => request<CallieTurn>('/callie/messages', json(body, 'POST')),
  applyProposal: (id: string, body: { reason: string; overrideReason?: string; editedBy: string | null }) => request<CallieProposal>(`/callie/proposals/${id}/apply`, json(body, 'POST')),
  dismissProposal: (id: string) => request<CallieProposal>(`/callie/proposals/${id}/dismiss`, { method: 'POST' }),
  myStints: (abbreviation: string, from: string, to: string) => request<MyStint[]>(`/physicians/${encodeURIComponent(abbreviation)}/stints?from=${from}&to=${to}`),
  savePlan: (abbreviation: string, siteId: string, stintStart: string, body: TripPlanInput) =>
    request<TripPlan>(`/physicians/${encodeURIComponent(abbreviation)}/stints/${siteId}/${stintStart}/plan`, json(body, 'PUT')),
  // The roster (D39)
  rosterCatalog: () => request<RosterCatalog>('/roster/catalog'),
  roster: () => request<RosterRow[]>('/roster'),
  rosterOne: (abbreviation: string) => request<RosterPhysician>(`/roster/${encodeURIComponent(abbreviation)}`),
  rosterCreate: (body: RosterProfileInput & { abbreviation: string; firstName: string; lastName: string; editedBy: string | null }) => request<RosterPhysician>('/roster', json(body, 'POST')),
  rosterUpdate: (abbreviation: string, body: RosterProfileInput & { editedBy: string | null }) => request<RosterPhysician>(`/roster/${encodeURIComponent(abbreviation)}`, json(body, 'PATCH')),
  rosterCredentialing: (abbreviation: string, siteId: string, body: { status: CredentialingStatus; extraSlots: string[]; editedBy: string | null }) => request<RosterPhysician>(`/roster/${encodeURIComponent(abbreviation)}/credentialing/${siteId}`, json(body, 'PUT')),
  rosterLicense: (abbreviation: string, state: string, body: { status: LicenseStatus; expiresOn: string | null; number: string | null; editedBy: string | null }) => request<RosterPhysician>(`/roster/${encodeURIComponent(abbreviation)}/licenses/${state}`, json(body, 'PUT')),
  rosterLogin: (abbreviation: string, editedBy: string | null) => request<{ email: string; existed: boolean; message: string }>(`/roster/${encodeURIComponent(abbreviation)}/login`, json({ editedBy }, 'POST')),
  // The customer tree (D38)
  orgClients: () => request<OrgUnit[]>('/org/clients'),
  orgSetUp: (body: { name: string; slug?: string; adoptExisting?: boolean }) =>
    request<{ client: OrgUnit; adopted: OrgAdoption | null }>('/org/setup', json(body, 'POST')),
  orgTree: (clientId: string) => request<OrgNode | null>(`/org/${clientId}/tree`),
  orgMemberCounts: (clientId: string) => request<Record<string, number>>(`/org/${clientId}/member-counts`),
  orgSummary: (clientId: string, unitId: string) => request<OrgSummary>(`/org/${clientId}/units/${unitId}/summary`),
  orgCreateUnit: (clientId: string, body: { parentId: string; kind: string; name: string; templateKey?: string | null; notes?: string | null }) =>
    request<OrgUnit>(`/org/${clientId}/units`, json(body, 'POST')),
  orgUpdateUnit: (clientId: string, unitId: string, body: { name?: string; templateKey?: string | null; notes?: string | null; active?: boolean }) =>
    request<OrgUnit>(`/org/${clientId}/units/${unitId}`, json(body, 'PATCH')),
  orgMoveUnit: (clientId: string, unitId: string, newParentId: string) =>
    request<OrgUnit[]>(`/org/${clientId}/units/${unitId}/move`, json({ newParentId }, 'POST')),
  orgSchedules: (clientId: string, unitId: string) => request<OrgSchedule[]>(`/org/${clientId}/units/${unitId}/schedules`),
  orgUpdateSchedule: (
    clientId: string,
    scheduleId: string,
    body: { name?: string; engineKey?: string; orgUnitId?: string; active?: boolean },
  ) =>
    request<OrgSchedule>(`/org/${clientId}/schedules/${scheduleId}`, json(body, 'PATCH')),
  orgScheduleContents: (clientId: string, scheduleId: string) =>
    request<{ empty: boolean; held: Record<string, number> }>(`/org/${clientId}/schedules/${scheduleId}/contents`),
  orgCreateSchedule: (clientId: string, body: { orgUnitId: string; name: string; engineKey: string; notes?: string | null }) =>
    request<OrgSchedule>(`/org/${clientId}/schedules`, json(body, 'POST')),
  orgMembers: (clientId: string, unitId: string, deep: boolean) =>
    request<OrgMember[]>(`/org/${clientId}/units/${unitId}/members${deep ? '?deep=1' : ''}`),
  orgPlaceable: (clientId: string, notIn: string) => request<OrgPerson[]>(`/org/${clientId}/placeable?notIn=${notIn}`),
  orgAddMember: (clientId: string, body: { physicianId: string; orgUnitId: string; role: string; isPrimary?: boolean }) =>
    request<OrgMember>(`/org/${clientId}/members`, json(body, 'POST')),
  orgRemoveMember: (clientId: string, membershipId: string) =>
    request<{ removed: string; newPrimary: string | null }>(`/org/${clientId}/members/${membershipId}`, { method: 'DELETE' }),
  orgSetPrimaryMember: (clientId: string, membershipId: string) =>
    request<OrgMember>(`/org/${clientId}/members/${membershipId}/primary`, { method: 'POST' }),
  // The residency (D43). Read-only from the screens: the block board is the
  // program's own document, uploaded rather than typed in here.
  residencySchedules: (clientId: string) => request<ResidencySchedule[]>(`/residency/${clientId}/schedules`),
  residencyBlocks: (clientId: string, scheduleId: string, year?: string) =>
    request<AcademicBlock[]>(`/residency/${clientId}/schedules/${scheduleId}/blocks${year ? `?year=${encodeURIComponent(year)}` : ''}`),
  residencyRotations: (clientId: string, scheduleId: string) =>
    request<ResidencyRotation[]>(`/residency/${clientId}/schedules/${scheduleId}/rotations`),
  residencyRoster: (clientId: string, scheduleId: string, year: string) =>
    request<ResidentYearRow[]>(`/residency/${clientId}/schedules/${scheduleId}/roster?year=${encodeURIComponent(year)}`),
  residencyAssignments: (clientId: string, scheduleId: string, opts: { year?: string; block?: number; physicianId?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.year) q.set('year', opts.year);
    if (opts.block) q.set('block', String(opts.block));
    if (opts.physicianId) q.set('physicianId', opts.physicianId);
    return request<BlockAssignmentRow[]>(`/residency/${clientId}/schedules/${scheduleId}/assignments?${q}`);
  },
  residencyAcgme: (clientId: string, scheduleId: string, year: string) =>
    request<AcgmeTotals[]>(`/residency/${clientId}/schedules/${scheduleId}/acgme?year=${encodeURIComponent(year)}`),
  // Uploading a year (D48). The file goes up as text and is read on the
  // server, so the screen never has a second opinion about what a column
  // heading means.
  importFormat: () => request<ImportFormat>('/residency/import/format'),
  residencyImportPreview: (clientId: string, scheduleId: string, file: string, opts: ImportChoices) =>
    request<ImportPreview>(`/residency/${clientId}/schedules/${scheduleId}/import/preview${importQuery(opts)}`, json({ file }, 'POST')),
  residencyImport: (clientId: string, scheduleId: string, file: string, opts: ImportChoices) =>
    request<ImportResult>(`/residency/${clientId}/schedules/${scheduleId}/import${importQuery(opts)}`, json({ file }, 'POST')),
  // Who runs a program (D45).
  // The evaluator's whole surface. `myPrograms` is how a login that is
  // nothing but a program role finds the program it was given a role on —
  // listing a customer's schedules needs schedule.read, which an evaluator
  // does not have.
  myPrograms: () => request<MyProgram[]>('/residency/my-programs'),
  assessmentRubric: () => request<Rubric>('/residency/assessment/rubric'),
  evaluableResidents: (clientId: string, scheduleId: string, year: string) =>
    request<EvaluableResident[]>(`/residency/${clientId}/schedules/${scheduleId}/evaluable?year=${encodeURIComponent(year)}`),
  myAssessments: (clientId: string, scheduleId: string) =>
    request<MyAssessments>(`/residency/${clientId}/schedules/${scheduleId}/assessments/mine`),
  recordReview: (clientId: string, scheduleId: string, physicianId: string, body: ReviewDraft) =>
    request<{ id: string }>(`/residency/${clientId}/schedules/${scheduleId}/residents/${physicianId}/reviews`, json(body, 'POST')),

  programRoleCatalog: () => request<RoleSpec[]>('/residency/roles/catalog'),
  programRoles: (clientId: string, scheduleId: string) => request<ProgramRole[]>(`/residency/${clientId}/schedules/${scheduleId}/roles`),
  grantProgramRole: (clientId: string, scheduleId: string, body: { email: string; role: string; notes?: string | null; grantedBy?: string | null }) =>
    request<ProgramRole>(`/residency/${clientId}/schedules/${scheduleId}/roles`, json(body, 'POST')),
  revokeProgramRole: (clientId: string, scheduleId: string, roleId: string, revokedBy: string | null) =>
    request<ProgramRole>(`/residency/${clientId}/schedules/${scheduleId}/roles/${roleId}`, json({ revokedBy }, 'DELETE')),
};

// ---- uploading a year (D48) ----

/** What the two switches on the import screen mean, in one place. */
export interface ImportChoices {
  /** merge speaks only for the resident-blocks in the file; replace clears the rest of the year. */
  mode: 'merge' | 'replace';
  /** Make rotations out of names the vocabulary does not know, rather than refusing them. */
  createUnknown: boolean;
}

const importQuery = (o: ImportChoices) => `?mode=${o.mode}${o.createUnknown ? '&createUnknown=1' : ''}`;

/** One column of the table form, as the reader itself defines it. */
export interface ImportColumn {
  field: string;
  label: string;
  about: string;
  names: string[];
  required: 'always' | 'identity' | 'optional';
}

export interface ImportFormat {
  columns: ImportColumn[];
  blocksPerYear: number;
}

/** How the file was read. Said out loud on the screen, because it is a reading rather than a fact. */
export interface ImportSource {
  format: 'json' | 'csv';
  notes: string[];
}

export interface ImportPreview {
  mode: 'merge' | 'replace';
  plan: {
    academicYear: string;
    blocks: number;
    residents: number;
    rotations: string[];
    assignments: number;
    unknownLabels: { label: string; count: number; residents: string[] }[];
  };
  diff: {
    newResidents: number;
    newBlocks: number;
    newRotations: number;
    residentsLeavingTheRoster: number;
    assignments: { created: number; changed: number; removed: number; unchanged: number };
  };
  problems: string[];
  warnings: string[];
  source: ImportSource;
}

export interface ImportResult {
  mode: 'merge' | 'replace';
  academicYear: string;
  blocks: number;
  rotations: number;
  residents: number;
  residentYears: number;
  assignments: { created: number; changed: number; removed: number; unchanged: number };
  warnings: string[];
  source: ImportSource;
}

/** The refusal shape both the reader and the import use: a sentence, then the list. */
export function problemsIn(e: unknown): string[] {
  const body = e instanceof ApiError ? e.body : null;
  if (body && typeof body === 'object' && Array.isArray((body as { problems?: unknown }).problems)) {
    return (body as { problems: string[] }).problems;
  }
  return [];
}

/** A role a program can hand out, and what it lets somebody do. */
export interface RoleSpec {
  key: string;
  label: string;
  about: string;
  capabilities: string[];
}

export interface ProgramRole {
  id: string;
  scheduleId: string;
  clientId: string;
  email: string;
  role: string;
  active: boolean;
  notes: string | null;
  grantedBy: string;
  grantedAt: string;
  label?: string;
  capabilities?: string[];
}

const CAPABILITY_LABELS: Record<string, string> = {
  'program.manage-roles': 'Manage who runs the program',
  'program.manage-evaluators': 'Appoint faculty evaluators',
  // Two different reads, and the difference is the whole point of the
  // evaluator role — so they must not both read as "assessments".
  'performance.read': "Read every resident's assessments",
  'performance.read-own': 'Read their own submissions',
  'performance.record': 'Record assessments',
  'schedule.read': 'Read the schedule',
  'schedule.import': 'Upload the schedule',
};

export function capabilityLabel(key: string): string {
  return CAPABILITY_LABELS[key] ?? key;
}


// ---- the evaluator login (D45) ----

/** A program this login holds a role on — the only programs it may know about. */
export interface MyProgram {
  clientId: string;
  scheduleId: string;
  scheduleName: string;
  unitName: string;
  role: string;
  label: string;
  capabilities: string[];
}

export interface ScalePoint { position: number; label: string }
export interface Scale { key: string; about: string; points: ScalePoint[] }
export interface RubricQuestion { key: string; number: number; text: string; scaleKey: string }
export interface Rubric {
  questions: RubricQuestion[];
  scales: Scale[];
  milestoneDomains: unknown[];
  periods: string[];
}

/** Whom this login may assess: a name, a level and a track. Never a rotation or a score. */
export interface EvaluableResident {
  physicianId: string;
  firstName: string;
  lastName: string;
  abbreviation: string;
  pgy: number;
  track: string;
}

export interface ReviewDraft {
  academicYear: string;
  period: string;
  answers: Record<string, string>;
  note?: string | null;
  recordedBy?: string;
}

export interface RecordedReview {
  id: string;
  academicYear: string;
  period: string;
  note: string | null;
  recordedAt: string;
  physician: { firstName: string; lastName: string; abbreviation: string };
  answers: { questionKey: string; scaleKey: string; position: number; label: string }[];
}

export interface MyAssessments {
  recordedBy: string;
  reviews: RecordedReview[];
  milestones: { id: string; academicYear: string; period: string; recordedAt: string; physician: { firstName: string; lastName: string } }[];
}

// ---- the residency (D43) ----

export interface ResidencySchedule {
  id: string;
  name: string;
  slug: string;
  orgUnitId: string;
  orgUnit: { name: string; path: string };
}

/** One two-week block of an academic year. */
export interface AcademicBlock {
  id: string;
  scheduleId: string;
  clientId: string;
  academicYear: string;
  number: number;
  startDate: string;
  endDate: string;
}

export interface ResidencyRotation {
  id: string;
  name: string;
  acgmeKind: string;
  clinical: boolean;
  active: boolean;
  notes: string | null;
  aliases: { alias: string }[];
  _count: { assignments: number };
}

export interface ResidentYearRow {
  id: string;
  pgy: number;
  track: string;
  active: boolean;
  physicianId: string;
  physician: { abbreviation: string; firstName: string; lastName: string };
}

export interface BlockAssignmentRow {
  id: string;
  /** What the program actually wrote, kept beside the rotation it resolved to. */
  sourceLabel: string | null;
  physicianId: string;
  block: { number: number; startDate: string; endDate: string; academicYear: string };
  rotation: { id: string; name: string; acgmeKind: string; clinical: boolean };
  physician: { abbreviation: string; firstName: string; lastName: string };
}

export interface AcgmeTotals {
  physicianId: string;
  abbreviation: string;
  blocks: number;
  clinicalBlocks: number;
  kinds: Record<string, number>;
}

/** The ACGME kinds, in the order a program reads them rather than alphabetically. */
export const ACGME_KINDS = ['critical-care', 'night-float', 'inpatient', 'ambulatory', 'subspecialty', 'research', 'leave', 'administrative', 'other'] as const;

const ACGME_LABELS: Record<string, string> = {
  'critical-care': 'Critical care',
  'night-float': 'Night float',
  inpatient: 'Inpatient',
  ambulatory: 'Ambulatory',
  subspecialty: 'Elective',
  research: 'Research',
  leave: 'Leave',
  administrative: 'Admin',
  other: 'Other',
};

export function acgmeLabel(kind: string): string {
  return ACGME_LABELS[kind] ?? kind;
}

/** "PGY-2 categorical" — how the program says it out loud. */
export function levelLabel(row: { pgy: number; track: string }): string {
  return `PGY-${row.pgy}${row.track === 'prelim' ? ' prelim' : ''}`;
}

/**
 * What to call a resident on screen (D48).
 *
 * A program that uploads names wants to read names; a program that uploads
 * "Resident 48" wants to read that. The awkward case is the second one
 * wearing the first one's clothes: an import with no names splits the key to
 * fill them, so "Resident 48" arrives as Resident / 48 and a screen that
 * trusts the fields prints "48, Resident".
 *
 * So the id wins whenever the name is nothing but the id taken apart. That
 * is an exact comparison, not a guess about what looks like a name — a real
 * resident called Trelawney is never mistaken for a split key, and a numbered
 * one is never given a surname of 48.
 */
export function personName(p: { firstName?: string | null; lastName?: string | null; abbreviation?: string | null }): string {
  const first = (p.firstName ?? '').trim();
  const last = (p.lastName ?? '').trim();
  const id = (p.abbreviation ?? '').trim();
  if (!first && !last) return id;
  if (id && `${first} ${last}`.trim() === id) return id;
  return [last, first].filter(Boolean).join(', ') || id;
}

// ---- the customer tree (D38) ----

/** One node of a client's tree: a client, a hospital, a department, a division, or a level nobody has named yet. */
export interface OrgUnit {
  id: string;
  parentId: string | null;
  kind: string;
  name: string;
  slug: string;
  clientId: string;
  path: string;
  depth: number;
  templateKey: string | null;
  active: boolean;
  notes: string | null;
}

/** The same node with its children nested beneath it, as the tree endpoint returns it. */
export interface OrgNode extends OrgUnit { children: OrgNode[] }

/** A named schedule on a unit. `engineKey` says which engine runs it, so a residency and a shift rota can share a parent. */
export interface OrgSchedule { id: string; orgUnitId: string; clientId: string; name: string; slug: string; engineKey: string; active: boolean }

export interface OrgPerson { id: string; abbreviation: string; firstName: string; lastName: string; status: string }

/** Somebody's seat in a unit. `isPrimary` marks the one that answers "where does this person sit". */
export interface OrgMember {
  id: string;
  physicianId: string;
  orgUnitId: string;
  role: string;
  isPrimary: boolean;
  active: boolean;
  physician: OrgPerson & { active: boolean };
  orgUnit: { id: string; name: string; kind: string };
}

/** A unit's headline numbers. `subtreeMembers` counts everybody beneath it, which is what a department means by "how many". */
export interface OrgSummary { unitId: string; directMembers: number; subtreeMembers: number; descendantUnits: number; schedules: number }

/** What a setup run took into the tree, so the screen can say what happened rather than just "done". */
export interface OrgAdoption { hospitals: number; schedules: number; weeksLinked: number; placed: number; credentialedSeats: number }

/** "department" → "Department", for the kind chips and the add form's dropdown. */
export function kindLabel(kind: string): string {
  return kind.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** What an engineKey means in words, since the key itself is for the machine. */
export function engineLabel(engineKey: string): string {
  if (engineKey === 'hospitalist-shift') return 'Hospitalist shifts';
  if (engineKey === 'im-residency') return 'Internal medicine residency';
  return kindLabel(engineKey);
}

// ---- tour logistics (D33) ----
export interface Person { id: string; abbreviation: string; firstName: string; lastName: string }
export interface Flight { airline: string; number: string; date: string; leg: 'arrival' | 'departure' }
/** Where a physician sleeps during a stint (D100). One answer, not two yes/no questions: a
 *  physician cannot both drive in from home and occupy a bed, and a capacity count built on
 *  answers that contradict each other is worse than no count at all. */
export type Lodging = 'home' | 'residence' | 'own';
export const LODGING_LABEL: Record<Lodging, string> = {
  home: 'Driving in from home',
  residence: 'Staying at the OSMD residence',
  own: 'Making my own arrangements',
};

/** What a physician told us about one stint — the "before you go" answers. */
export interface TripPlan {
  stintStart: string;
  needsArrivalTransfer: boolean | null; arrivalAirport: string | null; arrivalAt: string | null; arrivalDropoff: string | null;
  needsDepartureTransfer: boolean | null; departureAirport: string | null; departureAt: string | null; departurePickup: string | null;
  lodging: Lodging | null; flying: boolean | null; roomPreference: string | null;
  groceries: string[]; doorDashPass: boolean | null;
  flights: Flight[]; notes: string | null; submittedAt: string | null;
}
export type TripPlanInput = Partial<Omit<TripPlan, 'stintStart' | 'submittedAt' | 'groceries' | 'flights'>> & { groceries?: string[] | null; flights?: Flight[] | null };

/** How full the houses will be on one week (D100).
 *
 *  A "pool" is the houses and hospitals the `serves` links tie together — the Newburgh house
 *  and its three Deaconess hospitals are one pool with four bedrooms between them (D53). There
 *  is no company-wide percentage on purpose: it would read comfortably in the exact week one
 *  house is oversubscribed and another is half empty. */
export interface HousingPool {
  sites: { id: string; name: string }[];
  residences: { id: string; name: string; beds: number }[];
  beds: number;
  /** One row per night of the tour, the opening Tuesday through the Monday. */
  byNight: { date: string; needed: number }[];
  /** The most beds wanted on any one night — what the pool has to survive. */
  worstNight: number;
  /** null when the pool has no house at all: Vista has none yet (D68), and "3 physicians
   *  expect a bed where there are none" is the useful thing to say, not "0%". */
  percent: number | null;
  over: number;
  physicians: Person[];
}
export interface HousingCapacity {
  week: string;
  pools: HousingPool[];
  /** Working this week, has not said where they are staying. Deliberately kept out of every
   *  percentage above rather than guessed at. */
  unanswered: { physician: Person; site: string; stintStart: string }[];
  totals: { beds: number; worstNight: number; unanswered: number };
}
export interface Room { id: string; residenceId: string; name: string; notes: string | null; active: boolean }
export interface Residence {
  id: string; siteId: string; name: string; kind: string; address: string | null;
  /** The code itself — sensitive, shown to a physician only near arrival (D68). */
  accessNotes: string | null;
  /** How entry works when it is a rule rather than a secret — shown as soon as they have a room (D68). */
  entryMethod: string | null;
  contactName: string | null; contactPhone: string | null;
  wifi: string | null; parking: string | null; notes: string | null; active: boolean; rooms: Room[];
  /** Other hospitals housed here too (D53) — one house, one set of beds. */
  serves?: { siteId: string }[];
}
export type ResidenceInput = Omit<Residence, 'id' | 'siteId' | 'rooms' | 'active'> & { active?: boolean };
/** A picture of a residence (D73) — the stairs, the door, a room. Bytes live behind `photoUrl`. */
export interface ResidencePhoto {
  id: string;
  caption: string | null;
  contentType: string;
  width: number;
  height: number;
  uploadedBy: string | null;
  createdAt: string;
}
export interface SiteGuide { siteId: string; body: string; updatedAt: string | null; updatedBy: string | null }
/** Ride links (D36): open Uber or Lyft with the trip filled in. Present only when both ends have coordinates. */
export interface RideLinks { uber: string; lyft: string }
export interface Rides { arrival: RideLinks | null; arrivalTo: string | null; departure: RideLinks | null; departureFrom: string | null }
export type FlightState = 'scheduled' | 'delayed' | 'departed' | 'landed' | 'cancelled' | 'diverted' | 'unknown';
/** A flight the physician named, with its live status when tracking is on (D36). */
export interface FlightView extends Flight { tracked: boolean; status: FlightState; origin: string | null; destination: string | null; scheduledIn: string | null; estimatedIn: string | null; actualIn: string | null; arrivalDelayMin: number | null; checkedAt: string | null; summary: string }
export interface SiteAddress { id: string; name: string; address: string | null; lat: number | null; lng: number | null; located: boolean }
export interface LogisticsConfig { flights: { enabled: boolean; provider: string; problem?: string }; email: { enabled: boolean; from: string | null; office: string[] } }
export interface Occupant { stayId: string; physician: Person; arriveOn: string; departOn: string; status: string; note: string | null }
/** One hospital's week as the coordinator sees it: the sections of OSMD's "Tour Logistics & Handoffs" email. */
export interface LogisticsWeek {
  site: { id: string; name: string };
  week: string; closing: string;
  /** Only when nobody is at the hospital this week: the nearest weeks someone is. */
  nearest: { next: string | null; previous: string | null } | null;
  rotation: {
    ends: (Person & { positionSlots: string[]; hours: string })[];
    starts: (Person & { positionSlots: string[]; hours: string; through: string })[];
    staysOn: (Person & { hours: string; through: string })[];
  };
  handoffs: { positionSlot: string; from: Person | null; to: Person | null; at: string }[];
  nights: { date: string; positionSlot: string; physician: Person; hours: string }[];
  transportation: { physician: Person; stintStart: string; stintEnd: string; onSite: boolean; arriving: boolean; departing: boolean; plan: TripPlan | null; rides: Rides | null; flights: FlightView[] }[];
  housing: { id: string; name: string; kind: string; rooms: { id: string; name: string; occupants: Occupant[] }[] }[];
  unhoused: { physician: Person; stintStart: string; stintEnd: string }[];
  groceries: { physician: Person; items: string[]; doorDashPass: boolean | null }[];
  residences: Omit<Residence, 'rooms' | 'active' | 'siteId'>[];
  guide: { body: string; updatedAt: string; updatedBy: string } | null;
}
/** A physician's own stint with everything their "your week" card shows. */
export interface MyStint {
  siteId: string; siteName: string; start: string; end: string; status: string; slots: string[];
  /** The hospital's own details (D68): who to ring, the airport its physicians fly into, how lodging works with no OSMD house. */
  sitePhone: string | null; arrivalAirport: string | null; lodgingNote: string | null;
  handoff: { takesOverFrom: string | null; handsOffTo: string | null; at: string };
  plan: TripPlan | null;
  stay: {
    residence: string; room: string; status: string;
    roommates: { name: string; room: string; arriveOn: string; departOn: string }[];
    /** Only from three days before arrival until departure. */
    address: string | null; accessNotes: string | null;
    /** How entry works — a rule, not a secret, so it arrives with the room rather than near the door (D68). */
    entryMethod: string | null;
    contactName: string | null; contactPhone: string | null;
    wifi: string | null; parking: string | null; notes: string | null;
    /** Pictures of the house, on the same three-day rule as the address (D73). Null id outside it. */
    residenceId: string | null;
    photos: ResidencePhoto[];
  } | null;
  guide: string | null;
  /** The hospital's own documents (D98); empty once the stint is over. */
  documents: SiteDocument[];
  /** No `rides` here (D62): booking the airport ride is the coordinator's, so the links live only on the logistics week. */
  flights: FlightView[];
}

/**
 * A residence picture as a blob URL (D73).
 *
 * The login travels in a header, and a browser sends no headers of its own for `<img src>` —
 * so the bytes are fetched like any other call and handed to the tag as an object URL. The
 * caller revokes it when the picture leaves the screen.
 */
export interface SiteDocument {
  id: string;
  name: string;
  contentType: string;
  /** A PDF opens in a tab; a Word file is always a download (the bytes only prove it is a ZIP). */
  inline: boolean;
  sizeBytes: number;
  uploadedBy?: string | null;
  createdAt?: string;
}

/**
 * Open a hospital's document (D98). A browser sends no headers of its own for a plain link, so
 * the bytes are fetched with the login attached and handed over as a blob — a PDF to a new tab,
 * anything else as a download.
 */
export async function openSiteDocument(siteId: string, doc: SiteDocument): Promise<void> {
  const token = await idToken();
  const res = await fetch(`/api/sites/${siteId}/documents/${doc.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const body = safeJson(await res.text());
    throw new ApiError(res.status, body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string' ? (body as { message: string }).message : `That document could not be opened (${res.status})`, body);
  }
  const url = URL.createObjectURL(await res.blob());
  if (doc.inline) {
    window.open(url, '_blank', 'noopener');
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.name}${doc.contentType === 'application/msword' ? '.doc' : '.docx'}`;
    a.click();
  }
  // Revoked after the browser has had it; immediate revocation races the open.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function fileSizeLabel(bytes: number): string {
  return bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

export async function photoObjectUrl(residenceId: string, photoId: string): Promise<string> {
  const token = await idToken();
  const res = await fetch(`/api/residences/${residenceId}/photos/${photoId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const body = safeJson(await res.text());
    throw new ApiError(res.status, (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') ? (body as { message: string }).message : `${res.status} ${res.statusText}`, body);
  }
  return URL.createObjectURL(await res.blob());
}

/** Fetch a file the API generates (Excel, zip) with the login attached, and hand it to the browser as a download. */
export async function download(path: string, fallbackName: string): Promise<void> {
  const token = await idToken();
  const res = await fetch(`/api${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const body = safeJson(await res.text());
    throw new ApiError(res.status, (body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'string') ? (body as { message: string }).message : `${res.status} ${res.statusText}`, body);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---- dashboard, hospitals, timesheets (D31) ----
/**
 * A shift on the home page's card. Carries its own id, date and week (D93) so the card can open
 * the same editor the grid does — the card's `period` is not always the shift's own week.
 */
export interface OnDuty {
  id: string;
  date: string;
  periodStart: string;
  weekStatus: string;
  positionSlot: string;
  physician: PhysicianSummary;
  start: string | null;
  end: string | null;
  crossesMidnight: boolean;
}
export interface DashboardCard {
  siteId: string; name: string; letter: string;
  period: { periodStart: string; status: string; source: string } | null;
  physicians: number; expectedPositions: number; openSlotDays: number; openToday: string[];
  onDuty: OnDuty[];
  flags: { errors: number; warnings: number };
}
export interface Dashboard {
  today: string; week: string; weekEnd: string; focusDay: string;
  cards: DashboardCard[];
  /** Only when nothing is scheduled this week: the nearest weeks that are. */
  nearest: { next: string | null; previous: string | null } | null;
  attention: {
    openSlots: { siteId: string; siteName: string; periodStart: string; week: string; open: number; positions: string[] }[];
    unpublished: { siteId: string; siteName: string; periodStart: string; status: string }[];
    credentialing: { physician: PhysicianSummary; siteId: string; siteName: string; week: string; issue: string }[];
    licenses: { physician: PhysicianSummary; state: string; expiresOn: string; status: string; warning: 'expired' | 'expiring' }[];
    onboarding: { abbreviation: string; firstName: string; lastName: string; startDate: string | null; scheduled: boolean }[];
    requestWindows: { quarter: string; closesOn: string; open: boolean; responded: number; physicians: number }[];
    awaitingApproval: { siteId: string; siteName: string; periodStart: string; sheets: number; questions: number; approved: number }[];
  };
  recent: { at: string; kind: string; who: string; what: string; why: string | null; siteId: string | null; periodStart: string | null; quarter: string | null }[];
}
/** One hospital's quarter, day by day — whole Tuesday weeks, not calendar months (D67). */
export interface SiteQuarter {
  site: { id: string; name: string; letter: string };
  quarter: string;
  from: string;
  to: string;
  slots: string[];
  days: { date: string; shifts: { id: string; positionSlot: string; physician: PhysicianSummary | null; start: string | null; end: string | null; crossesMidnight: boolean; origin: string; periodStart: string; status: string }[] }[];
  periods: { periodStart: string; status: string }[];
}
export interface DayHours { date: string; day: number; swing: number; night: number; note: string }
/** scheduled = straight from the schedule; recorded = the office corrected it; approved = signed off by the office (D35). */
export type SheetStatus = 'scheduled' | 'recorded' | 'approved';
export interface TourSheet {
  siteId: string; siteName: string; periodStart: string; periodEnd: string;
  physician: { abbreviation: string; firstName: string; lastName: string };
  scheduled: DayHours[]; recorded: DayHours[] | null; note: string | null;
  status: SheetStatus; recordedBy: string | null; recordedAt: string | null;
  question: string | null; questionAt: string | null; questionResolvedBy: string | null; questionResolvedAt: string | null;
  /** The physician's own confirmation that the hours look right (D57). Separate from the office's sign-off below. */
  physicianApprovedBy: string | null; physicianApprovedAt: string | null;
  approvedBy: string | null; approvedAt: string | null;
  totals: { scheduled: number; recorded: number | null };
}
export interface TourSummary { siteId: string; siteName: string; periodStart: string; periodEnd: string; status: SheetStatus; scheduledHours: number; recordedHours: number | null; question: string | null; questionResolvedAt: string | null; physicianApprovedAt: string | null; approvedAt: string | null }

/** Who the API says I am, from the verified login (or the open local mode). */
/** One customer this login may work in, and the kinds of work it holds. */
export interface Tenant {
  id: string;
  name: string;
  engines: string[];
}

export interface Me {
  mode: 'cognito' | 'off';
  email: string | null;
  name: string | null;
  /** The scheduling office's address for a physician's questions (D35), when the service has one. */
  officeEmail: string | null;
  admin: boolean;
  /** The clients this login may act in; null means every one (an owner). (D38) */
  clients: string[] | null;
  /**
   * The KIND of work in this login's reach — the engines of the schedules it
   * can see. The navigation is built from this rather than from the client,
   * because one client can hold both a residency and an attending rota. (D38)
   */
  engines: string[];
  /**
   * The same reach, per customer (D49). `engines` above is the union, which
   * is right for a customer's own people — they are in exactly one — and
   * wrong for an owner, who is in all of them and would otherwise carry
   * every tab of every product forever.
   */
  tenants: Tenant[];
  /**
   * The programs this login holds a role on (D45).
   *
   * Not the same as `engines`, which follows the schedules a login can SEE. A
   * faculty evaluator can see no schedule at all and still holds a role on a
   * program — without this the screens could not tell such a login from one
   * with no business here.
   */
  programRoles: MyProgram[];
  /** How `clients` was decided: owner | granted | founding | none. (D38) */
  scopeOrigin: 'owner' | 'granted' | 'founding' | 'none';
  /** CallCovered's own people: everything a scheduler can do, plus platform settings later. */
  owner: boolean;
  /** Logistics coordinators (lodging, transfers, groceries) — schedulers and owners count too. */
  logistics: boolean;
  physician: PhysicianSummary | null;
  unknownAbbreviation: string | null;
}

/** "2027-Q2" → "Q2 2027". */
export function quarterLabel(q: string): string {
  const [y, n] = q.split('-Q');
  return `Q${n} ${y}`;
}

/** Human labels for slot ids. Unknown ids fall back to a tidied version of the id. */
export function slotLabel(slot: string): string {
  const known: Record<string, string> = {
    'night-backup': 'Night Backup',
    'night-first-call': 'Night First Call',
    'backup-night-md': 'Backup Night MD',
    pulmcrit: 'Pulm/Crit',
    cardiology: 'Cardiology',
    'trauma-surgery': 'Trauma Surgery',
    night: 'Night',
    'night-am': 'Night–AM',
    daytime: 'Daytime',
    swing: 'Swing',
  };
  if (known[slot]) return known[slot];
  const m = slot.match(/^rounder-(\d+)$/);
  if (m) return `Rounder ${m[1]}`;
  return slot.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "07:00" → "7a", "19:00" → "7p", "15:30" → "3:30p" — the shorthand OSMD's own schedule uses. */
export function hourLabel(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h < 12 ? 'a' : 'p';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hour12}:${String(m).padStart(2, '0')}${suffix}` : `${hour12}${suffix}`;
}

/** "7a–7p · 12h", "7a–7a · 24h", "7p–7a · 12h" — hours plus how long the shift is. */
export function hoursLabel(start: string, end: string, crossesMidnight: boolean): string {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  let minutes = toMin(end) - toMin(start);
  if (crossesMidnight || minutes <= 0) minutes += 24 * 60;
  const h = minutes / 60;
  return `${hourLabel(start)}–${hourLabel(end)} · ${Number.isInteger(h) ? h : h.toFixed(1)}h`;
}

// ---- Callie (D34) ----
export interface CallieAction { tool: string; input: Record<string, unknown>; changed: boolean; error?: string }
/** A schedule change Callie described; the scheduler applies it from the card with their own reason. */
export interface CallieProposal { id: string; kind: string; siteId: string; periodStart: string; summary: string; introduced: string[]; blocked: string[]; status: string; outcome?: string | null }
export interface CallieTurn { conversationId: string; reply: string; actions: CallieAction[]; proposals: CallieProposal[] }

// ---- The roster (D39) -------------------------------------------------------------------
export type CredentialingStatus = 'credentialed' | 'in-process' | 'none';
export type LicenseStatus = 'active' | 'pending' | 'none';
export interface RosterCatalog {
  sites: { id: string; name: string; licenseState: string | null; standardSlots: string[]; backupSlot: string | null; extraSlots: string[]; allSlots: string[] }[];
  states: string[];
  statesInUse: string[];
  logins: { enabled: boolean; problem?: string };
}
export interface RosterLicense { state: string; status: LicenseStatus; expiresOn: string | null; number?: string | null; updatedBy?: string; updatedAt?: string; warning: 'expired' | 'expiring' | null }
export interface RosterRow {
  abbreviation: string; firstName: string; lastName: string; email: string | null; status: string; employmentType: string | null; onboardingStartDate: string | null;
  loginCreatedAt: string | null;
  /** Whether this person can actually sign in — from the login service itself (D95). */
  hasLogin: boolean;
  /** False when the login service could not be reached, so `hasLogin` is the local record only. */
  loginKnown: boolean;
  credentialing: { siteId: string; status: CredentialingStatus; extraSlots: string[] }[];
  licenses: RosterLicense[];
  attention: string[];
}
export interface RosterProfileInput {
  firstName?: string; lastName?: string; email?: string | null; phone?: string | null; employmentType?: 'W2' | '1099' | null;
  status?: 'Active' | 'Onboarding' | 'Inactive'; onboardingStartDate?: string | null; takesNightBackup?: boolean | null;
  standardDayShiftHours?: 12 | 16 | null; typicalBlockLength?: '1 week' | '2 weeks' | 'Either' | null; maxConsecutiveWeeks?: number | null; targetWeeksPerQuarter?: number | null; notes?: string | null;
}
export interface RosterPhysician {
  id: string; abbreviation: string; firstName: string; lastName: string; email: string | null; phone: string | null; employmentType: string | null; status: string; active: boolean;
  onboardingStartDate: string | null; takesNightBackup: boolean | null; standardDayShiftHours: number | null; typicalBlockLength: string | null; maxConsecutiveWeeks: number | null; targetWeeksPerQuarter: number | null; notes: string | null;
  loginCreatedAt: string | null;
  credentialing: { siteId: string; siteName: string; licenseState: string | null; status: CredentialingStatus; extraSlots: string[] }[];
  licenses: RosterLicense[];
  changes: { what: string; changedBy: string; changedAt: string; before: unknown; after: unknown }[];
}
